import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import get from 'lodash/get';

import Session from 'shared_modules/Session';

import { PreAuthsResponse, PreAuthWithSale } from '../types';

interface UseCustomerPreauthsReturn {
  preauths: PreAuthWithSale[];
  oldestPreauth: PreAuthWithSale | null;
  otherPreauths: PreAuthWithSale[];
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const useCustomerPreauths = (
  customerId: string,
  currentTransactionId?: string
): UseCustomerPreauthsReturn => {
  const [preauths, setPreauths] = useState<PreAuthWithSale[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSales = async (saleRefs: string[]): Promise<Record<string, Sale>> => {
    if (saleRefs.length === 0) {
      return {};
    }

    const accountId = window.merchantos?.account?.id || Session.accountID();
    if (!accountId) {
      return {};
    }

    try {
      const uniqueSaleRefs = [...new Set(saleRefs)];
      const limit = Math.min(uniqueSaleRefs.length, 100);
      const relations = encodeURIComponent('["SaleLines","SaleLines.Item"]');
      const { data } = await axios.get(
        `/API/V3/Account/${accountId}/Sale.json?saleID=IN,${uniqueSaleRefs.join(
          ','
        )}&load_relations=${relations}&limit=${limit}`
      );

      let salesData: Sale[] = [];
      if (Array.isArray(data.Sale)) {
        salesData = data.Sale;
      } else if (data.Sale) {
        salesData = [data.Sale];
      }

      const salesMap: Record<string, Sale> = {};
      salesData.forEach((sale: Sale) => {
        if (sale.saleID) {
          salesMap[sale.saleID] = sale;
        }
      });

      return salesMap;
    } catch (err) {
      return {};
    }
  };

  const fetchPreauths = useCallback(async () => {
    if (!customerId) {
      setIsError(true);
      setError('Customer ID is required');
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // Fetch preauths once
      const { data } = await axios.get<PreAuthsResponse>('/admin/payments/pre-authorizations', {
        params: { customer: customerId },
      });

      const preauthsData = data.data || [];

      // Fetch all sales in a single batch request
      const saleRefs = preauthsData.map((preauth) => preauth.saleRef).filter(Boolean);
      const salesMap = await fetchSales(saleRefs);

      // Map sales to preauths
      const preauthsWithSales: PreAuthWithSale[] = preauthsData.map((preauth) => ({
        ...preauth,
        sale: salesMap[preauth.saleRef] || null,
      }));

      // Sort by captureDeadline (oldest first)
      preauthsWithSales.sort((a, b) => {
        const dateA = new Date(a.captureDeadline).getTime();
        const dateB = new Date(b.captureDeadline).getTime();
        return dateA - dateB;
      });

      setPreauths(preauthsWithSales);
    } catch (err) {
      setIsError(true);
      const errorMessage = get(err, 'response.data.message', 'Failed to fetch Preauthorizations');
      setError(errorMessage);
      setPreauths([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchPreauths();
  }, [fetchPreauths]);

  // Determine which preauth to capture
  let oldestPreauth: PreAuthWithSale | null = null;
  let otherPreauths: PreAuthWithSale[] = [];

  if (preauths.length > 0) {
    // If currentTransactionId is provided and matches a preauth, prioritize it
    if (currentTransactionId) {
      const matchingPreauth = preauths.find((p) => p.saleRef === currentTransactionId);
      if (matchingPreauth) {
        oldestPreauth = matchingPreauth;
        otherPreauths = preauths.filter((p) => p.saleRef !== currentTransactionId);
      } else {
        // No match found, use oldest by captureDeadline
        oldestPreauth = preauths[0];
        otherPreauths = preauths.slice(1);
      }
    } else {
      // No currentTransactionId, use oldest by captureDeadline
      oldestPreauth = preauths[0];
      otherPreauths = preauths.slice(1);
    }
  }

  return {
    preauths,
    oldestPreauth,
    otherPreauths,
    isLoading,
    isError,
    error,
    refetch: fetchPreauths,
  };
};

export default useCustomerPreauths;
